import React, { useState, useEffect } from 'react'
import { collection, setDoc, doc, deleteDoc, updateDoc, arrayUnion, query, where, getDoc, getDocs, runTransaction } from 'firebase/firestore'
import { db, functions } from '../firebaseConfig'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons' // Tambah faTimes
import { useLocation, useNavigate } from 'react-router-dom';
import { uploadPdfFile } from '../utils/uploadPdfFile'
import { ATTACHMENT_ACCEPT, ATTACHMENT_MAX_SIZE_BYTES, isValidAttachmentFile, mergeAttachmentsToPdf } from '../utils/attachmentUpload'
import { useTheme } from '../context/ThemeContext'
import { getUnitCode } from '../constants/businessUnits'
import { BBM_PRICE_PER_LITER } from '../constants/bbmPrice'


const RbsUmumForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isEditMode = location.state?.isEditMode || false;
    const editData = location.state?.editData || null;

    const { theme } = useTheme()
    const [todayDate, setTodayDate] = useState('')
    const [userData, setUserData] = useState({
        uid: '',
        nama: '',
        bankName: '',
        accountNumber: '',
        unit: [], // Sekarang array
        validator: [],
        reviewer1: [],
        reviewer2: [],
        platKendaraan: []
    })

    const [isSubmitting, setIsSubmitting] = useState(false)
    // Bagian AN: catatan wajib diisi editor saat mengedit pengajuan milik
    // orang lain -- jejak "kesalahan apa yang diperbaiki" di statusHistory.
    const [editNote, setEditNote] = useState('')

    const initialReimbursementState = {
        jenis: '',
        biaya: '',
        item: '',
        keterangan: '',
        plat: '',
        liter: '',
        tanggal: '',
        isLainnya: false,
        jenisLain: '',
        tanggalPengajuan: todayDate
    }

    // Bagian CA: baris item bisa pre-filled dari "Catat Nota" (nota yang
    // sudah dicatat sebelumnya, dirangkai jadi 1 pengajuan RBS) -- lihat
    // CatatNota.jsx `rangkaiRbs()`. `jenis` di-set via isLainnya/jenisLain
    // (bukan dropdown jenisOptions) karena kategori nota bebas teks, tidak
    // dijamin cocok salah satu opsi baku.
    // Bagian CC: field pre-fill disamakan dengan skema nota di
    // CatatNota.jsx (isLainnya/namaItem/jenisLain/biaya/liter/item/
    // keterangan/plat/tanggal), bukan kategori bebas teks lagi -- supaya
    // dropdown "Jenis" di sini otomatis terisi benar (bukan selalu jatuh ke
    // "Lainnya"). `jenis` dikonstruksi ulang sebagai {value,label} -- react-
    // select mencocokkan lewat `value`, tidak perlu referensi objek yang
    // sama persis dengan array `jenisOptions`.
    const prefillRbsItems = location.state?.prefillRbsItems || null
    const [reimbursements, setReimbursements] = useState(() => {
        if (prefillRbsItems && prefillRbsItems.length > 0) {
            return prefillRbsItems.map((p) => ({
                ...initialReimbursementState,
                isLainnya: p.isLainnya,
                jenis: p.isLainnya ? '' : { value: p.namaItem, label: p.namaItem },
                jenisLain: p.isLainnya ? (p.jenisLain || 'Lainnya') : '',
                biaya: String(p.biaya || ''),
                liter: p.liter ? String(p.liter) : '',
                item: p.item || '',
                keterangan: p.keterangan || '',
                plat: p.plat || '',
                tanggal: p.tanggal || ''
            }))
        }
        return [initialReimbursementState]
    })

    useEffect(() => {
        if (todayDate) {
            setReimbursements((prevReimbursements) =>
                prevReimbursements.map((item) => ({ ...item, tanggalPengajuan: todayDate }))
            )
        }
    }, [todayDate])

    // --- State untuk Multi File Upload ---
    const [attachmentFiles, setAttachmentFiles] = useState([])

    const [selectedUnit, setSelectedUnit] = useState(null)
    const [userUnitOptions, setUserUnitOptions] = useState([])
    const [isAdmin, setIsAdmin] = useState(false)
    const [isSuperAdmin, setIsSuperAdmin] = useState(false)

    const [validatorOptions, setValidatorOptions] = useState([])
    const [selectedValidator, setSelectedValidator] = useState(null)

    const [reviewerOptions, setReviewerOptions] = useState([])
    const [selectedReviewer1, setSelectedReviewer1] = useState(null)
    const [selectedReviewer2, setSelectedReviewer2] = useState(null)

    // --- Fetch data validator & reviewer untuk semua role ---
    useEffect(() => {
        const fetchValidators = async () => {
            try {
                const usersRef = collection(db, 'userDirectory')
                const q = query(usersRef, where('role', 'in', ['Validator', 'Admin', 'Reviewer']))
                const querySnapshot = await getDocs(q)

                const options = querySnapshot.docs.map((doc) => {
                    const userData = doc.data()
                    return {
                        value: userData.uid,
                        label: userData.nama,
                        role: userData.role
                    }
                })

                setValidatorOptions(options)
            } catch (error) {
                console.error('Error fetching validators:', error)
                toast.error('Gagal memuat daftar validator')
            }
        }

        fetchValidators()
    }, [])

    useEffect(() => {
        const fetchReviewer = async () => {
            try {
                const usersRef = collection(db, 'userDirectory')
                const q = query(usersRef, where('role', 'in', ['Reviewer', 'Validator', 'Admin']))
                const querySnapshot = await getDocs(q)

                const options = querySnapshot.docs.map((doc) => {
                    const userData = doc.data()
                    return {
                        value: userData.uid,
                        label: userData.nama,
                        role: userData.role
                    }
                })

                setReviewerOptions(options)
            } catch (error) {
                console.error('Error fetching reviewers:', error)
                toast.error('Gagal memuat daftar reviewer')
            }
        }

        fetchReviewer()
    }, [])

    // Fetch semua plat kendaraan terdaftar (lintas user) untuk pilihan dropdown Plat Nomor (khusus item BBM)
    const [allPlatOptions, setAllPlatOptions] = useState([])
    useEffect(() => {
        const fetchAllPlat = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'userDirectory'))
                const platMap = new Map()

                querySnapshot.docs.forEach((docSnap) => {
                    const data = docSnap.data()
                    const plates = Array.isArray(data.platKendaraan) ? data.platKendaraan : []
                    plates.forEach((plat) => {
                        if (plat && !platMap.has(plat)) {
                            platMap.set(plat, data.nama ? `${plat} - ${data.nama}` : plat)
                        }
                    })
                })

                const options = Array.from(platMap.entries())
                    .map(([value, label]) => ({ value, label }))
                    .sort((a, b) => a.value.localeCompare(b.value))

                setAllPlatOptions(options)
            } catch (error) {
                console.error('Error fetching daftar plat kendaraan:', error)
            }
        }

        fetchAllPlat()
    }, [])

    // Logika Auto-Fill Validator & Reviewer untuk user dengan 1 Unit Bisnis
   useEffect(() => {
        if (userData.uid) { 
            // Auto-fill Validator
            if (validatorOptions.length > 0 && userData.validator?.length > 0) {
                const defaultValidator = validatorOptions.find(opt => userData.validator.includes(opt.value));
                if (defaultValidator) setSelectedValidator(defaultValidator);
            }
            // Auto-fill Reviewer 1
            if (reviewerOptions.length > 0 && userData.reviewer1?.length > 0) {
                const defaultRev1 = reviewerOptions.find(opt => userData.reviewer1.includes(opt.value));
                if (defaultRev1) setSelectedReviewer1(defaultRev1);
            }
            // Auto-fill Reviewer 2
            if (reviewerOptions.length > 0 && userData.reviewer2?.length > 0) {
                const defaultRev2 = reviewerOptions.find(opt => userData.reviewer2.includes(opt.value));
                if (defaultRev2) setSelectedReviewer2(defaultRev2);
            }
        }
    }, [userData, validatorOptions, reviewerOptions]);

    const isSingleUnit = !isSuperAdmin && userUnitOptions.length === 1;

    const BUSINESS_UNITS = [
        { value: 'PT Makassar Jaya Samudera', label: 'PT Makassar Jaya Samudera' },
        { value: 'PT Samudera Makassar Logistik', label: 'PT Samudera Makassar Logistik' },
        { value: 'PT Kendari Jaya Samudera', label: 'PT Kendari Jaya Samudera' },
        { value: 'PT Samudera Kendari Logistik', label: 'PT Samudera Kendari Logistik' },
        { value: 'PT Samudera Agencies Indonesia', label: 'PT Samudera Agencies Indonesia' },
        { value: 'PT SILKargo Indonesia', label: 'PT SILKargo Indonesia' },
        { value: 'PT PAD Samudera Perdana', label: 'PT PAD Samudera Perdana' },
        { value: 'PT Masaji Kargosentra Tama', label: 'PT Masaji Kargosentra Tama' },
        { value: 'Samudera Indonesia', label: 'Samudera Indonesia' },
        { value: 'Panitia', label: 'Panitia' }
    ]

    const jenisOptions = [
        { value: 'ATK', label: 'ATK' },
        { value: 'RTG', label: 'RTG' },
        { value: 'RTK', label: 'RTK' },
        { value: 'Entertaint', label: 'Entertaint' },
        { value: 'Parkir', label: 'Parkir' },
        { value: 'E-Toll', label: 'E-Toll' },
        { value: 'BBM Pertalite', label: 'BBM Pertalite' },
        { value: 'BBM Pertamax', label: 'BBM Pertamax' },
        { value: 'BBM Pertamax Turbo', label: 'BBM Pertamax Turbo' },
        { value: 'BBM Solar', label: 'BBM Solar' },
        { value: 'Meals Lembur', label: 'Meals Lembur' },
        { value: 'Meals Meeting', label: 'Meals Meeting' },
        { value: 'CSR', label: 'CSR' },
        { value: 'Utilitas', label: 'Utilitas' },
        { value: 'Fasilitas', label: 'Fasilitas' },
        { value: 'IT M&R', label: 'IT M&R' },
        { value: 'Lainnya', label: 'Lainnya' }
    ]

    const isBbmJenis = (item) => !item.isLainnya && !!BBM_PRICE_PER_LITER[item.jenis?.value]

    const parseRupiahValue = (value) => {
        if (!value) return 0
        return Number(String(value).replace(/[^0-9,]/g, '').replace(',', '.')) || 0
    }

    // Bagian BN: dipakai field Liter (input teks, bukan type="number" lagi)
    // supaya koma ("10,5") ikut diterima sebagai pemisah desimal -- lihat
    // handleInputChange. Cuma izinkan digit & SATU titik; titik kedua dst
    // dan karakter lain dibuang.
    const normalizeDecimalInput = (value) => {
        const withDot = value.replace(',', '.')
        const firstDotIndex = withDot.indexOf('.')
        if (firstDotIndex === -1) return withDot.replace(/[^\d]/g, '')
        const intPart = withDot.slice(0, firstDotIndex).replace(/[^\d]/g, '')
        const decPart = withDot.slice(firstDotIndex + 1).replace(/[^\d]/g, '')
        return `${intPart}.${decPart}`
    }

    // Liter dihitung otomatis dari biaya yang diinput dibagi harga/liter jenis BBM terpilih.
    // Tetap bisa diedit manual (mis. kalau harga di struk beda dari patokan).
    const calculateLiter = (biayaValue, jenisValue) => {
        const price = BBM_PRICE_PER_LITER[jenisValue]
        const biayaNumber = parseRupiahValue(biayaValue)
        if (!price || !biayaNumber) return ''
        return (biayaNumber / price).toFixed(2)
    }

    useEffect(() => {
        const today = new Date()
        const formattedDate = today.toISOString().split('T')[0]
        const uid = localStorage.getItem('userUid')

        setTodayDate(formattedDate)

        const fetchUserData = async () => {
            try {
                const userDocRef = doc(db, 'users', uid)
                const userDoc = await getDoc(userDocRef)

                if (userDoc.exists()) {
                    const data = userDoc.data()
                    const adminStatus = data.role === 'Admin' || data.role === 'Super Admin'
                    setIsAdmin(adminStatus)
                    setIsSuperAdmin(data.role === 'Super Admin')

                    const userUnitsArray = Array.isArray(data.unit) ? data.unit : (data.unit ? [data.unit] : [])

                    setUserData({
                        uid: data.uid || '',
                        nama: data.nama || '',
                        bankName: data.bankName || '',
                        accountNumber: data.accountNumber || '',
                        unit: userUnitsArray,
                        department: data.department || [],
                        validator: data.validator || [],
                        reviewer1: data.reviewer1 || [],
                        reviewer2: data.reviewer2 || [],
                        platKendaraan: Array.isArray(data.platKendaraan) ? data.platKendaraan : []
                    })

                    const unitOptionsForUser = userUnitsArray.map(u => ({ value: u, label: u }))
                    setUserUnitOptions(unitOptionsForUser)

                    // Bagian CI: Unit Bisnis sudah dipilih di "Catat Nota" --
                    // ikuti itu, jangan ketiban auto-select single-unit di bawah.
                    const prefillUnit = location.state?.prefillUnit
                    if (prefillUnit) {
                        setSelectedUnit({ value: prefillUnit, label: prefillUnit })
                    } else if (!adminStatus && unitOptionsForUser.length === 1) {
                        setSelectedUnit(unitOptionsForUser[0])
                    } else if (!adminStatus && unitOptionsForUser.length === 0) {
                        setSelectedUnit(null)
                    }
                }
            } catch (error) {
                console.error('Error fetching user data:', error)
            }
        }

        fetchUserData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }).format(date)
    }

    const resetForm = () => {
        setReimbursements([
            {
                ...initialReimbursementState,
                tanggalPengajuan: todayDate
            }
        ])

        const fileInputs = document.querySelectorAll('input[type="file"]')
        fileInputs.forEach(input => input.value = '')

        setAttachmentFiles([])

        if (isAdmin || userUnitOptions.length > 1) {
            setSelectedUnit(null)
        }
        
        setSelectedValidator(null)
        setSelectedReviewer1(null)
        setSelectedReviewer2(null)
    }

    const formatRupiah = (number) => {
        // Cegah error jika data kosong (undefined/null)
        if (number === undefined || number === null) return ''

        // Paksa ubah ke bentuk teks (String) sebelum di-replace
        const stringNumber = number.toString()
        const strNumber = stringNumber.replace(/[^,\d]/g, '')
        
        const split = strNumber.split(',')
        const sisa = split[0].length % 3
        let rupiah = split[0].substr(0, sisa)
        const ribuan = split[0].substr(sisa).match(/\d{3}/gi)

        if (ribuan) {
            const separator = sisa ? '.' : ''
            rupiah += separator + ribuan.join('.')
        }

        rupiah = split[1] !== undefined ? rupiah + ',' + split[1] : rupiah
        return rupiah ? 'Rp' + rupiah : ''
    }

    const handleAddForm = () => {
        setReimbursements([...reimbursements, { ...initialReimbursementState, tanggalPengajuan: todayDate }])
    }

    const handleRemoveForm = (index) => {
        const updatedReimbursements = reimbursements.filter((_, i) => i !== index)
        setReimbursements(updatedReimbursements)
    }

    const handleInputChange = (index, field, value) => {
        let formattedValue = value

        if (field === 'biaya') {
            formattedValue = formatRupiah(value)
        }

        // Bagian BN: lihat catatan di normalizeDecimalInput -- field Liter
        // sekarang input teks, koma dinormalisasi jadi titik di sini.
        if (field === 'liter') {
            formattedValue = normalizeDecimalInput(value)
        }

        const updatedReimbursements = reimbursements.map((item, i) => {
            if (i !== index) return item
            const updatedItem = { ...item, [field]: formattedValue }
            if (field === 'biaya' && isBbmJenis(item)) {
                updatedItem.liter = calculateLiter(formattedValue, item.jenis.value)
            }
            return updatedItem
        })
        setReimbursements(updatedReimbursements)
    }

    const handleJenisChange = (index, selectedOption) => {
        const updatedReimbursements = [...reimbursements]

        if (selectedOption && selectedOption.value === 'Lainnya') {
            updatedReimbursements[index] = {
                ...updatedReimbursements[index],
                jenis: null,
                isLainnya: true,
                jenisLain: ''
            }
        } else {
            const isBbm = !!BBM_PRICE_PER_LITER[selectedOption?.value]
            const currentPlat = updatedReimbursements[index].plat
            const defaultPlat = isBbm && !currentPlat && userData.platKendaraan?.length === 1
                ? userData.platKendaraan[0]
                : currentPlat

            updatedReimbursements[index] = {
                ...updatedReimbursements[index],
                jenis: selectedOption,
                isLainnya: false,
                jenisLain: '',
                liter: isBbm ? calculateLiter(updatedReimbursements[index].biaya, selectedOption?.value) : updatedReimbursements[index].liter,
                plat: isBbm ? defaultPlat : updatedReimbursements[index].plat
            }
        }

        setReimbursements(updatedReimbursements)
    }

    const handleJenisLainChange = (index, value) => {
        const updatedReimbursements = [...reimbursements]
        updatedReimbursements[index].jenisLain = value
        setReimbursements(updatedReimbursements)
    }


    // PENTING: nomor dokumen HARUS didapat dari counter atomik (runTransaction), bukan
    // Math.random() - dengan random, dua pengajuan di unit & hari yang sama punya peluang
    // nyata mendapat nomor (dan path lampiran) yang identik. Firestore akan otomatis retry
    // transaksi ini kalau ada konflik baca-tulis, jadi dua submit tidak mungkin dapat nomor
    // akhir yang sama. Pola ini meniru counter atomik yang sudah dipakai di FormBs.jsx.
    const generateDisplayId = async () => {
        const unitCode = selectedUnit ? getUnitCode(selectedUnit.value) : 'UNKNOWN'
        const today = new Date()
        const year = today.getFullYear().toString()
        const month = (today.getMonth() + 1).toString().padStart(2, '0')
        const day = today.getDate().toString().padStart(2, '0')
        const counterRef = doc(db, 'businessUnitCounters', `${unitCode}_RBS_GAU`)

        // Bagian AV: sama seperti FormBs.jsx (Bagian AO/AP) -- counter TIDAK
        // SELALU sinkron dengan nomor yang benar-benar sudah terpakai di
        // displayIdOwners. TIDAK membaca displayIdOwners sama sekali (rule-nya
        // melarang total get/list) -- coba tulis, kalau ditolak karena
        // nomornya sudah dipakai, retry DI LUAR transaksi dengan offset
        // nomor berikutnya (lihat catatan lengkap di FormRbsBbm.jsx).
        const attemptGenerate = (offset) => runTransaction(db, async (transaction) => {
            const counterDoc = await transaction.get(counterRef)
            const baseNumber = (!counterDoc.exists() || counterDoc.data().lastResetYear !== year)
                ? 1
                : counterDoc.data().lastNumber + 1
            const candidateNumber = baseNumber + offset

            transaction.set(counterRef, { lastNumber: candidateNumber, lastResetYear: year })

            const newDisplayId = `RBS.GAU.${unitCode}.${year.slice(-2)}${month}${day}.${candidateNumber.toString().padStart(4, '0')}`
            // Dicatat di transaksi yang sama supaya storage.rules bisa memvalidasi
            // kepemilikan lampiran/PDF lewat firestore.get() cross-service (lihat 18.6)
            transaction.set(doc(db, 'displayIdOwners', newDisplayId), { uid: localStorage.getItem('userUid') })

            return newDisplayId
        })

        const MAX_ATTEMPTS = 50
        let lastError = null
        for (let offset = 0; offset < MAX_ATTEMPTS; offset++) {
            try {
                return await attemptGenerate(offset)
            } catch (error) {
                if (error?.code !== 'permission-denied') throw error
                lastError = error
            }
        }
        throw lastError || new Error(`Tidak menemukan nomor dokumen yang tersedia untuk ${unitCode} setelah ${MAX_ATTEMPTS} percobaan`)
    }

    // --- Fungsi handler upload untuk multi file ---
    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files)
        if (!files.length) return

        const validFiles = []
        for (let file of files) {
            if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
                toast.error(`Ukuran file ${file.name} maksimal ${Math.round(ATTACHMENT_MAX_SIZE_BYTES / (1024 * 1024))}MB`)
                continue
            }
            if (!(await isValidAttachmentFile(file))) {
                toast.error(`File ${file.name} bukan PDF/JPG/PNG yang valid`)
                continue
            }
            validFiles.push(file)
        }

        setAttachmentFiles(prev => [...prev, ...validFiles])
        event.target.value = ''
    }

    const removeAttachment = (indexToRemove) => {
        setAttachmentFiles(prev => prev.filter((_, index) => index !== indexToRemove))
    }

    // --- Gabungkan semua lampiran (PDF/JPG/PNG) jadi SATU file PDF sebelum diupload,
    // supaya tombol "Lihat Lampiran" di Detail selalu membuka 1 file gabungan ---
    const uploadAttachments = async (files, displayId) => {
        if (!files || files.length === 0) return null

        try {
            const mergedFile = await mergeAttachmentsToPdf(files, `Lampiran_${displayId}.pdf`)
            return await uploadPdfFile(
                functions,
                `Reimbursement/GA_Umum/${displayId}/${mergedFile.name}`,
                mergedFile,
                { mode: 'displayIdOwners', displayId }
            )
        } catch (error) {
            console.error('Error uploading files:', error?.code, error?.message, error)
            toast.error(error?.message || 'Gagal mengunggah lampiran')
            return null
        }
    }

    useEffect(() => {
        if (isEditMode && editData && editData.reimbursements) {
            
            // 1. Set Item Reimbursement
            const formattedReimbursements = editData.reimbursements.map(item => ({
                ...item,
                biaya: item.biaya?.toString() || '',
                liter: item.liter?.toString() || '',
                plat: item.plat || '',
                jenis: typeof item.jenis === 'string'
                        ? { value: item.jenis, label: item.jenis }
                        : item.jenis,
            }));
            setReimbursements(formattedReimbursements);

            // 2. Paksa set data user pengaju (Gunakan setTimeout agar tidak tertimpa data login)
            setTimeout(() => {
                if (editData.user) {
                    setUserData({
                        uid: editData.user.uid,
                        nama: editData.user.nama,
                        bankName: editData.user.bankName || '',
                        accountNumber: editData.user.accountNumber || '',
                        department: editData.user.department || '',
                        platKendaraan: []
                    });

                    // 2b. Ambil plat kendaraan terdaftar terbaru milik pengaju (bukan dari snapshot lama)
                    if (editData.user.uid) {
                        getDoc(doc(db, 'users', editData.user.uid)).then((submitterDoc) => {
                            if (submitterDoc.exists()) {
                                const submitterPlat = submitterDoc.data().platKendaraan
                                setUserData((prev) => ({
                                    ...prev,
                                    platKendaraan: Array.isArray(submitterPlat) ? submitterPlat : []
                                }))
                            }
                        }).catch((error) => console.error('Error fetching plat kendaraan pengaju:', error))
                    }

                    // 3. Set Dropdown Unit
                    if (editData.user.unit) {
                        setSelectedUnit({ value: editData.user.unit, label: editData.user.unit });
                    }

                    // 4. Set Dropdown Approval (Cari label berdasarkan value yang cocok di opsi)
                    const findOption = (options, val) => options.find(o => o.value === val) || { value: val, label: val };

                    if (editData.user.validator) {
                        setSelectedValidator(findOption(validatorOptions, editData.user.validator[0]));
                    }
                    if (editData.user.reviewer1) {
                        setSelectedReviewer1(findOption(reviewerOptions, editData.user.reviewer1[0]));
                    }
                    if (editData.user.reviewer2) {
                        setSelectedReviewer2(findOption(reviewerOptions, editData.user.reviewer2[0]));
                    }
                }
            }, 100); 
        }
    }, [isEditMode, editData, validatorOptions, reviewerOptions]);

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true)

            if (selectedReviewer1 && selectedReviewer2 && selectedReviewer1.value === selectedReviewer2.value) {
                toast.warning('Reviewer 1 dan Reviewer 2 tidak boleh sama')
                setIsSubmitting(false)
                return
            }

            const missingFields = []

            // Validasi seragam untuk semua role
            if (!userData.nama) missingFields.push('Nama')
            if (!selectedUnit?.value) missingFields.push('Unit Bisnis')
            if (!selectedValidator) missingFields.push('Validator')
            if (!selectedReviewer1) missingFields.push('Reviewer 1')
            if (!selectedReviewer2) missingFields.push('Reviewer 2')

            const multipleItems = reimbursements.length > 1

            reimbursements.forEach((r, index) => {
                const getFieldLabel = (baseLabel) => {
                    return multipleItems ? `${baseLabel} (Item ${index + 1})` : baseLabel
                }

                if (r.isLainnya) {
                    if (!r.jenisLain) missingFields.push(getFieldLabel('Jenis Reimbursement'))
                    if (!r.biaya) missingFields.push(getFieldLabel('Biaya'))
                    if (!r.item) missingFields.push(getFieldLabel('Item'))
                    if (!r.tanggal) missingFields.push(getFieldLabel('Tanggal Aktivitas'))
                } else if (isBbmJenis(r)) {
                    if (!r.jenis) missingFields.push(getFieldLabel('Jenis Reimbursement'))
                    if (!r.biaya) missingFields.push(getFieldLabel('Biaya'))
                    if (!r.plat) missingFields.push(getFieldLabel('Plat Kendaraan'))
                    if (!r.liter) missingFields.push(getFieldLabel('Liter'))
                    if (!r.tanggal) missingFields.push(getFieldLabel('Tanggal Aktivitas'))
                } else {
                    if (!r.jenis) missingFields.push(getFieldLabel('Jenis Reimbursement'))
                    if (!r.biaya) missingFields.push(getFieldLabel('Biaya'))
                    if (!r.item) missingFields.push(getFieldLabel('Item'))
                    if (!r.tanggal) missingFields.push(getFieldLabel('Tanggal Aktivitas'))
                }
            })

            if (!isEditMode && attachmentFiles.length === 0) {
                missingFields.push('File Lampiran')
            }

            if (isEditMode && !editNote.trim()) missingFields.push('Catatan Perubahan')

            if (missingFields.length > 0) {
                missingFields.forEach((field) => {
                    toast.warning(
                        <>
                            Mohon lengkapi <b>{field}</b>
                        </>
                    )
                })

                setIsSubmitting(false)
                return
            }

            const displayId = isEditMode ? editData.displayId : await generateDisplayId()

            const mergedLampiranUrl = await uploadAttachments(attachmentFiles, displayId)

            const totalBiaya = reimbursements.reduce((total, item) => {
                const biayaNumber = parseInt(item.biaya.replace(/[^0-9]/g, ''))
                return total + biayaNumber
            }, 0)

            const parseRupiah = (value) => {
                return Number(value.replace(/[^,\d]/g, '').replace(',', '.')) || 0
            }

            const reimbursementData = {
                user: {
                    uid: userData.uid,
                    nama: userData.nama,
                    bankName: userData.bankName,
                    accountNumber: userData.accountNumber,
                    unit: selectedUnit.value,
                    unitCode: getUnitCode(selectedUnit.value),
                    department: userData.department,
                    validator: [selectedValidator.value],
                    reviewer1: [selectedReviewer1.value],
                    reviewer2: [selectedReviewer2.value]
                },
                reimbursements: reimbursements.map((item) => ({
                    biaya: parseRupiah(item.biaya),
                    item: item.item,
                    keterangan: item.keterangan,
                    plat: item.plat || '',
                    liter: Number(item.liter) || 0,
                    tanggal: item.tanggal,
                    isLainnya: item.isLainnya,
                    jenis: item.isLainnya ? item.jenisLain : item.jenis?.value || item.jenis
                })),
                displayId: displayId,
                kategori: 'GA/Umum',
                status: 'Diajukan',
                approvedByReviewer1: false,
                approvedByReviewer2: false,
                approvedBySuperAdmin: false,
                rejectedBySuperAdmin: false,
                tanggalPengajuan: todayDate,
                // --- Lampiran digabung jadi 1 file PDF sebelum diupload ---
                lampiran: [`Lampiran_${displayId}.pdf`],
                lampiranUrl: mergedLampiranUrl,
                totalBiaya: totalBiaya,
                statusHistory: [
                    {
                        status: 'Diajukan',
                        timestamp: new Date().toISOString(),
                        actor: userData.uid
                    }
                ],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            }

            if (isEditMode) {
                // JIKA EDIT: Gunakan updateDoc
                const reimbursementRef = doc(db, 'reimbursement', editData.id)

                // Bagian AN: userData.uid di mode edit adalah uid PENGAJU ASLI,
                // bukan uid editor yang login -- pakai localStorage langsung.
                const editorUid = localStorage.getItem('userUid')
                const editorRole = localStorage.getItem('userRole') || 'Admin'

                let updateData = {
                    // Bagian AT: sebelumnya `user` (unit/validator/reviewer1/
                    // reviewer2) TIDAK PERNAH ikut disertakan di sini --
                    // dropdown Validator/Reviewer tetap bisa diklik & diganti
                    // saat edit, tapi perubahannya SELALU dibuang diam-diam
                    // saat disimpan. firestore.rules keepsWorkflowIdentity()
                    // cuma menjaga user.uid & displayId tetap sama --
                    // validator/reviewer1/reviewer2 di dalam map user memang
                    // boleh berubah.
                    user: reimbursementData.user,
                    reimbursements: reimbursementData.reimbursements,
                    totalBiaya: reimbursementData.totalBiaya,
                    statusHistory: arrayUnion({
                        status: `Data Diubah oleh ${editorRole}`,
                        timestamp: new Date().toISOString(),
                        actor: editorUid,
                        reason: editNote.trim()
                    })
                }

                // Update lampiran hanya jika Super Admin mengunggah file baru
                if (attachmentFiles.length > 0) {
                    updateData.lampiran = reimbursementData.lampiran;
                    updateData.lampiranUrl = reimbursementData.lampiranUrl;
                }

                await updateDoc(reimbursementRef, updateData)
                toast.success('Reimbursement GA/Umum berhasil diperbarui!')
                
                setIsSubmitting(false)
                navigate('/reimbursement/cek-pengajuan')

            } else {
                // JIKA BIKIN BARU: satu kali tulis saja (create), BUKAN addDoc lalu
                // setDoc lagi ke ID yang sama. Firestore menganggap setDoc kedua itu
                // sebagai operasi UPDATE (karena dokumennya sudah ada), dan rule
                // canUpdateWorkflow() menolak Employee biasa yang baru submit
                // dokumennya sendiri -- itu sebabnya toast error muncul padahal
                // dokumennya sudah kadung tersimpan dari addDoc pertama.
                const newDocRef = doc(collection(db, 'reimbursement'))
                await setDoc(newDocRef, { ...reimbursementData, id: newDocRef.id })

                // Bagian CA: nota draft yang baru dirangkai jadi RBS ini sudah
                // "terpakai" -- hapus supaya tidak nongol dobel di Catat Nota.
                // Kegagalan hapus di sini tidak boleh membuat submit terlihat
                // gagal (RBS-nya sudah kebentuk) -- dicoba lagi manual oleh
                // user lewat halaman Catat Nota kalau perlu.
                const prefillNotaDraftIds = location.state?.prefillNotaDraftIds
                if (Array.isArray(prefillNotaDraftIds) && prefillNotaDraftIds.length > 0) {
                    try {
                        await Promise.all(prefillNotaDraftIds.map((id) => deleteDoc(doc(db, 'notaDraft', id))))
                    } catch (cleanupError) {
                        console.error('Error cleaning up notaDraft after RBS submit:', cleanupError)
                    }
                }

                toast.success('Reimbursement GA/Umum berhasil diajukan!')

                resetForm()
                setIsSubmitting(false)
            }
            
        } catch (error) {
            console.error('Error submitting reimbursement:', error?.code, error?.message, error)
            toast.error(error?.message || 'Terjadi kesalahan saat menyimpan data. Silakan coba lagi.')
            setIsSubmitting(false)
        }
    }

    // --- Tampilan UI untuk Multi Upload ---
    const renderFileUpload = () => {
        return (
            <div className="flex flex-col items-start w-full">
                <div className="flex flex-col xl:flex-row items-start xl:items-center w-full">
                    <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        accept={ATTACHMENT_ACCEPT}
                        multiple
                        onChange={handleFileUpload}
                    />
                    <label
                        htmlFor="file-upload"
                        className="w-full xl:w-fit text-center h-full xl:h-10 px-4 py-4 xl:py-2 bg-gray-50 dark:bg-gray-700 xl:bg-gray-200 dark:xl:bg-gray-700 border dark:border-gray-600 rounded-md cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 hover:border-gray-400 transition duration-300 ease-in-out dark:text-gray-200"
                    >
                        Upload File
                    </label>
                    <span className="ml-0 xl:ml-4 text-gray-500 dark:text-gray-400 mt-2 xl:mt-0 text-sm">
                        Format .pdf/.jpg/.png, bisa lebih dari 1 file (Max Size: {Math.round(ATTACHMENT_MAX_SIZE_BYTES / (1024 * 1024))}MB/file)
                    </span>
                </div>
                
                {attachmentFiles.length > 0 && (
                    <div className="mt-3 w-full">
                        {attachmentFiles.map((file, index) => (
                            <div key={index} className="flex justify-between items-center bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded mb-2 border border-gray-200 dark:border-gray-600">
                                <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[80%]">{file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => removeAttachment(index)}
                                    className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 ml-2 font-bold"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    const isDark = theme === 'dark'
    const customStyles = {
        control: (base, state) => ({
            ...base,
            padding: '0 7px',
            height: '40px',
            minHeight: '40px',
            borderColor: isDark ? '#4b5563' : '#e5e7eb',
            backgroundColor: state.isDisabled ? (isDark ? '#374151' : '#f9fafb') : (isDark ? '#1f2937' : 'white'),
            cursor: state.isDisabled ? 'not-allowed' : 'default',
            '&:hover': {
                borderColor: state.isDisabled ? (isDark ? '#4b5563' : '#e5e7eb') : '#3b82f6'
            }
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 7px',
            height: '40px',
            minHeight: '40px'
        }),
        singleValue: (base) => ({ ...base, color: isDark ? '#f3f4f6' : '#111827' }),
        input: (base) => ({ ...base, color: isDark ? '#f3f4f6' : '#111827' }),
        placeholder: (base) => ({ ...base, color: isDark ? '#9ca3af' : '#6b7280' }),
        menu: (base) => ({ ...base, zIndex: 100, backgroundColor: isDark ? '#1f2937' : '#ffffff' }),
        // Bagian AX: menuPortalTarget={document.body} butuh z-index tinggi di sini
        // juga (bukan cuma di `menu`) supaya dropdown tidak tampil tertutup/salah
        // posisi kalau Select ini dirender di dalam modal/overlay lain.
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            backgroundColor: isDark
                ? (state.isSelected ? '#374151' : state.isFocused ? '#2d3748' : '#1f2937')
                : base.backgroundColor,
            color: isDark ? '#f3f4f6' : base.color,
            cursor: 'pointer'
        })
    }

    return (
        <div className="container mx-auto py-10 md:py-8">
            <h2 className="text-xl font-medium mb-4 dark:text-gray-100">
                Tambah <span className="font-bold">Reimbursement GA/Umum</span>
            </h2>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow transition-colors">
                {/* --- Layout diseragamkan untuk semua role --- */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 xl:gap-6 mb-2 lg:mb-3">
                    {/* Row 1 */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Nama Lengkap</label>
                        <input
                            className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                            type="text"
                            value={userData.nama}
                            disabled
                        />
                    </div>
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Validator <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={validatorOptions}
                            value={selectedValidator}
                            onChange={setSelectedValidator}
                            placeholder="Pilih Validator"
                            className="basic-single"
                            classNamePrefix="select"
                            styles={customStyles}
                            isSearchable={true}
                            isClearable={true}
                            menuPortalTarget={document.body}
                            menuPosition="absolute"
                            isDisabled={isSingleUnit}
                        />
                    </div>

                    {/* Row 2 */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Nomor Rekening</label>
                        <input
                            className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                            type="text"
                            value={userData.accountNumber}
                            disabled
                        />
                    </div>
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Reviewer 1 <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={reviewerOptions}
                            value={selectedReviewer1}
                            onChange={setSelectedReviewer1}
                            placeholder="Pilih Reviewer 1"
                            className="basic-single"
                            classNamePrefix="select"
                            styles={customStyles}
                            isSearchable={true}
                            isClearable={true}
                            menuPortalTarget={document.body}
                            menuPosition="absolute"
                            isDisabled={isSingleUnit}
                        />
                    </div>

                    {/* Row 3 */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Nama Bank</label>
                        <input
                            className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                            type="text"
                            value={userData.bankName}
                            disabled
                        />
                    </div>
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Reviewer 2 <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={reviewerOptions}
                            value={selectedReviewer2}
                            onChange={setSelectedReviewer2}
                            placeholder="Pilih Reviewer 2"
                            className="basic-single"
                            classNamePrefix="select"
                            styles={customStyles}
                            isSearchable={true}
                            isClearable={true}
                            menuPortalTarget={document.body}
                            menuPosition="absolute"
                            isDisabled={isSingleUnit}
                        />
                    </div>

                    {/* Row 4 */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">Tanggal Pengajuan</label>
                        <input
                            className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
                            type="text"
                            value={formatDate(todayDate)}
                            disabled
                        />
                    </div>
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Lampiran <span className="text-red-500">*</span>
                        </label>
                        {renderFileUpload()}
                    </div>

                    {/* Row 5 */}
                    <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Unit Bisnis <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={isSuperAdmin ? BUSINESS_UNITS : userUnitOptions}
                            value={selectedUnit}
                            onChange={setSelectedUnit}
                            placeholder="Pilih Unit Bisnis"
                            className="basic-single"
                            classNamePrefix="select"
                            styles={customStyles}
                            isSearchable={true}
                            menuPortalTarget={document.body}
                            menuPosition="absolute"
                        />
                    </div>
                </div>

                <hr className="border-gray-300 dark:border-gray-600 my-6" />

                {reimbursements.map((reimbursement, index) => (
                    <div key={index}>
                        {index > 0 && (
                            <hr className="border-gray-300 dark:border-gray-600 my-6 block xl:hidden" />
                        )}

                        <div className="flex flex-col xl:flex-row justify-stretch gap-2 mb-2">
                            <div className="flex-1 w-full xl:min-w-44">
                                {(index === 0 || window.innerWidth < 1280) && (
                                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2 xl:hidden">
                                        Jenis Reimbursement <span className="text-red-500">*</span>
                                    </label>
                                )}
                                {index === 0 && (
                                    <label className="hidden xl:block text-gray-700 dark:text-gray-300 font-medium mb-2">
                                        Jenis Reimbursement <span className="text-red-500">*</span>
                                    </label>
                                )}
                                <div key={index}>
                                    {reimbursement.isLainnya ? (
                                        <input
                                            type="text"
                                            placeholder="Jenis lain"
                                            value={reimbursement.jenisLain}
                                            onChange={(e) => handleJenisLainChange(index, e.target.value)}
                                            className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:border-blue-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                                        />
                                    ) : (
                                        <Select
                                            options={jenisOptions}
                                            value={reimbursement.jenis}
                                            onChange={(selectedOption) => handleJenisChange(index, selectedOption)}
                                            placeholder="Pilih jenis..."
                                            className="w-full"
                                            styles={customStyles}                                            
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            menuPosition="absolute"
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="flex-1 w-full xl:max-w-36">
                                {(index === 0 || window.innerWidth < 1280) && (
                                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2 xl:hidden">
                                        Biaya <span className="text-red-500">*</span>
                                    </label>
                                )}
                                {index === 0 && (
                                    <label className="hidden xl:block text-gray-700 dark:text-gray-300 font-medium mb-2">
                                        Biaya <span className="text-red-500">*</span>
                                    </label>
                                )}
                                <input
                                    className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:border-blue-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                                    type="text"
                                    value={formatRupiah(reimbursement.biaya)}
                                    onChange={(e) => handleInputChange(index, 'biaya', e.target.value)}
                                />
                            </div>

                            {isBbmJenis(reimbursement) ? (
                                <>
                                    <div className="flex-1 w-full xl:max-w-36">
                                        {(index === 0 || window.innerWidth < 1280) && (
                                            <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2 xl:hidden">
                                                Plat Nomor <span className="text-red-500">*</span>
                                            </label>
                                        )}
                                        {index === 0 && (
                                            <label className="hidden xl:block text-gray-700 dark:text-gray-300 font-medium mb-2">
                                                Plat Nomor <span className="text-red-500">*</span>
                                            </label>
                                        )}
                                        <CreatableSelect
                                            isClearable
                                            options={allPlatOptions}
                                            value={reimbursement.plat ? { value: reimbursement.plat, label: reimbursement.plat } : null}
                                            onChange={(selectedOption) => {
                                                const raw = selectedOption ? selectedOption.value : ''
                                                const filteredValue = raw.toUpperCase().replace(/[^A-Z0-9\s]/g, '')
                                                handleInputChange(index, 'plat', filteredValue)
                                            }}
                                            placeholder="Pilih/ketik plat..."
                                            formatCreateLabel={(input) => `Gunakan "${input.toUpperCase()}"`}
                                            styles={customStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="absolute"
                                        />
                                    </div>

                                    <div className="flex-1 w-full xl:max-w-28">
                                        {(index === 0 || window.innerWidth < 1280) && (
                                            <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2 xl:hidden">
                                                Liter <span className="text-red-500">*</span>
                                            </label>
                                        )}
                                        {index === 0 && (
                                            <label className="hidden xl:block text-gray-700 dark:text-gray-300 font-medium mb-2">
                                                Liter <span className="text-red-500">*</span>
                                            </label>
                                        )}
                                        <input
                                            className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:border-blue-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="mis. 10,5"
                                            value={reimbursement.liter}
                                            onChange={(e) => handleInputChange(index, 'liter', e.target.value)}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="flex-1 w-full xl:min-w-36">
                                    {(index === 0 || window.innerWidth < 1280) && (
                                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2 xl:hidden">
                                            Item <span className="text-red-500">*</span>
                                        </label>
                                    )}
                                    {index === 0 && (
                                        <label className="hidden xl:block text-gray-700 dark:text-gray-300 font-medium mb-2">
                                            Item <span className="text-red-500">*</span>
                                        </label>
                                    )}
                                    <input
                                        className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:border-blue-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                                        type="text"
                                        value={reimbursement.item}
                                        onChange={(e) => handleInputChange(index, 'item', e.target.value)}
                                    />
                                </div>
                            )}

                            <div className="flex-1 w-full xl:min-w-36">
                                {(index === 0 || window.innerWidth < 1280) && (
                                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2 xl:hidden">Keterangan</label>
                                )}
                                {index === 0 && (
                                    <label className="hidden xl:block text-gray-700 dark:text-gray-300 font-medium mb-2">Keterangan</label>
                                )}
                                <textarea
                                    className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:border-blue-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none resize-none"
                                    style={{scrollbarWidth: 'none'}}
                                    type="text"
                                    value={reimbursement.keterangan}
                                    onChange={(e) => handleInputChange(index, 'keterangan', e.target.value)}
                                />
                            </div>

                            <div className="flex-1 w-full xl:max-w-40">
                                {(index === 0 || window.innerWidth < 1280) && (
                                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2 xl:hidden">
                                        Tanggal Aktivitas <span className="text-red-500">*</span>
                                    </label>
                                )}
                                {index === 0 && (
                                    <label className="hidden xl:block text-gray-700 dark:text-gray-300 font-medium mb-2">
                                        Tanggal Aktivitas <span className="text-red-500">*</span>
                                    </label>
                                )}
                                <input
                                    className="w-full h-10 px-4 py-2 border dark:border-gray-600 rounded-md bg-transparent text-gray-900 dark:text-gray-100 hover:border-blue-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                                    type="date"
                                    value={reimbursement.tanggal}
                                    onChange={(e) => handleInputChange(index, 'tanggal', e.target.value)}
                                />
                            </div>

                            <div className="flex items-end my-2 xl:max-w-20 xl:my-0">
                                {(index === 0 || window.innerWidth < 1280) && (
                                    <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2 xl:hidden"> </label>
                                )}
                                {index === 0 && (
                                    <label className="hidden xl:block text-gray-700 dark:text-gray-300 font-medium mb-2">&nbsp;</label>
                                )}
                                <button
                                    className="w-full h-10 px-4 py-2 bg-transparent text-red-500 dark:text-red-400 border border-red-500 dark:border-red-500 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 transition duration-300"
                                    onClick={() => handleRemoveForm(index)}
                                >
                                    Hapus
                                </button>
                            </div>
                        </div>
                    </div>
                ))}

                <div className="mb-4 w-full text-center xl:text-start">
                    <span
                        className="text-red-600 dark:text-red-400 font-bold underline cursor-pointer hover:text-red-700 dark:hover:text-red-300"
                        onClick={handleAddForm}
                    >
                        Tambah
                    </span>
                </div>

                {isEditMode && (
                    <div className="mt-4">
                        <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
                            Catatan Perubahan (Alasan Edit) <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md hover:border-blue-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none h-20 px-4 py-2"
                            placeholder="Jelaskan kesalahan/perubahan yang diperbaiki pada pengajuan ini"
                            value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                        />
                    </div>
                )}

                <hr className="border-gray-300 dark:border-gray-600 my-6" />

                <div className="flex justify-end mt-6">
                    <button
                        className={`w-full xl:w-fit rounded text-white py-3
                        ${isSubmitting ? 'px-8 bg-red-700 cursor-not-allowed' : 'px-16 bg-red-600 hover:bg-red-700 hover:text-gray-200'}
                        flex items-center justify-center relative`}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <div className="flex items-center gap-1 text-gray-200">
                                <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                                Submitting...
                            </div>
                        ) : (
                            'Submit'
                        )}
                    </button>
                </div>
            </div>

        </div>
    )
}
export default RbsUmumForm